import {
  BookMarked,
  Bot,
  Clock,
  Dices,
  House,
  Layers,
  NotebookPen,
  Trophy,
  User,
  Users,
  Video,
  Compass,
  type LucideIcon,
} from "lucide-react";

/**
 * The single source of truth for app navigation.
 *
 * Three surfaces render navigation and all three read from here:
 *
 *   - `GlobalNavSidebar`   — the desktop rail (md+)
 *   - `MobileBottomNav`    — the tab bar (< md)
 *   - `PageHeader`'s sheet — the hamburger drawer (< md)
 *
 * They used to carry three hand-maintained item lists that had drifted apart
 * (different labels, different membership, different active tests), so a route
 * could appear in one surface and be missing from another. Deriving all three
 * from this file is what keeps them honest.
 *
 * ## Matching is declarative
 *
 * Each item declares the path prefixes that light it up (`match`), plus any
 * child prefixes that must *not* (`exclude`). This replaces two hand-written
 * exclusion lists inside the sidebar that had to be edited by hand every time
 * a route was added under `/my-topics/*` or `/vocabulary/*`.
 */

export interface NavItemDef {
  /** Stable id — also the React key. */
  id: string;
  name: string;
  icon: LucideIcon;
  /** Where clicking the row navigates. */
  path: string;
  /** Path prefixes that activate this row. */
  match: string[];
  /** Match by equality only, so `/` does not activate on `/topics`. */
  exact?: boolean;
  /** Prefixes that deactivate this row — use for children that own a row. */
  exclude?: string[];
  /** Extra icon tint, used sparingly for the Community section. */
  iconClassName?: string;
  /** Attribute for the product-tour walkthrough. */
  tour?: string;
  /** Show in the mobile bottom tab bar. */
  mobilePrimary?: boolean;
  /** Show in the mobile hamburger drawer. */
  inSheet?: boolean;
  /** Requires a signed-in learner; hidden from guests. */
  requiresAuth?: boolean;
  /**
   * Reachable but not rendered as a top-level row. Collections and Notes are
   * tabs of My Vocabulary, so they appear in the sidebar's expandable groups
   * (and in `VocabularyTabs`) rather than as rows of their own.
   */
  hidden?: boolean;
}

export interface NavSectionDef {
  id: string;
  label: string;
  items: NavItemDef[];
}

export const NAV_SECTIONS: NavSectionDef[] = [
  {
    id: "learn",
    label: "Learn",
    items: [
      {
        id: "home",
        name: "Home",
        icon: House,
        path: "/",
        match: ["/"],
        exact: true,
        tour: "nav-home",
        mobilePrimary: true,
        inSheet: true,
      },
      {
        id: "browse",
        name: "Browse",
        icon: Compass,
        path: "/topics",
        match: ["/topics"],
        tour: "nav-browse",
        mobilePrimary: true,
        inSheet: true,
      },
      {
        id: "videos",
        name: "Videos",
        icon: Video,
        path: "/videos",
        match: ["/videos"],
        tour: "nav-videos",
        inSheet: true,
      },
      {
        id: "random-question",
        name: "Roll & Talk",
        icon: Dices,
        path: "/roll-and-talk",
        match: ["/roll-and-talk", "/random-question"],
        tour: "nav-random-question",
        inSheet: true,
      },
    ],
  },
  {
    id: "my-space",
    label: "My Space",
    items: [
      {
        id: "my-topics",
        name: "My Topics",
        icon: User,
        path: "/my-topics",
        match: ["/my-topics"],
        // AI Chat and the Practice rows are their own destinations, and the
        // editor is a detail view rather than a place you "are".
        exclude: ["/my-topics/ai", "/my-topics/progress", "/my-topics/time", "/my-topics/speaking"],
        tour: "nav-my-topics",
        inSheet: true,
      },
      {
        id: "vocabulary",
        name: "My Vocabulary",
        icon: BookMarked,
        path: "/vocabulary",
        match: ["/vocabulary"],
        // Collections and Notes are sibling tabs of this feature.
        exclude: ["/vocabulary/collections", "/vocabulary/notes", "/vocabulary/practice"],
        tour: "nav-vocabulary",
        mobilePrimary: true,
        inSheet: true,
        requiresAuth: true,
      },
      {
        id: "collections",
        name: "Collections",
        icon: Layers,
        path: "/vocabulary/collections",
        match: ["/vocabulary/collections"],
        tour: "nav-collections",
        requiresAuth: true,
        hidden: true,
      },
      {
        id: "notes",
        name: "Notes",
        icon: NotebookPen,
        path: "/vocabulary/notes",
        match: ["/vocabulary/notes"],
        tour: "nav-notes",
        requiresAuth: true,
        hidden: true,
      },
      {
        id: "progress",
        name: "Progress",
        icon: Clock,
        path: "/my-topics/progress",
        match: ["/my-topics/progress", "/my-topics/time", "/my-topics/speaking"],
        tour: "nav-progress",
        requiresAuth: true,
      },
      {
        id: "study-groups",
        name: "Study Groups",
        icon: Users,
        path: "/study-groups",
        match: ["/study-groups"],
        tour: "nav-groups",
        mobilePrimary: true,
        inSheet: true,
      },
      {
        id: "ai-chat",
        name: "AI Chat",
        icon: Bot,
        path: "/my-topics/ai",
        match: ["/my-topics/ai"],
        tour: "nav-ai",
        inSheet: true,
      },
    ],
  },
  {
    id: "community",
    label: "Community",
    items: [
      {
        id: "leaderboard",
        name: "Leaderboard",
        icon: Trophy,
        path: "/leaderboard",
        match: ["/leaderboard"],
        iconClassName: "text-amber-500",
        tour: "nav-leaderboard",
        inSheet: true,
      },
    ],
  },
];

/** Every item, flattened — for lookups and deep links. */
export const NAV_ITEMS: NavItemDef[] = NAV_SECTIONS.flatMap((s) => s.items);

/** Sections that render as plain rows (`hidden` items filtered out). */
export const NAV_VISIBLE_SECTIONS: NavSectionDef[] = NAV_SECTIONS.map((s) => ({
  ...s,
  items: s.items.filter((i) => !i.hidden),
}));

/**
 * Sections rendered as plain rows.
 *
 * "My Space" is excluded: signed-in learners in expanded mode get the
 * expandable `MySpaceGroups` treatment instead, which only covers the groups
 * backed by a data source. Time and Speak therefore live in their own section
 * so they still render in every state.
 */
export const NAV_ROW_SECTIONS: NavSectionDef[] = NAV_VISIBLE_SECTIONS.filter(
  (s) => s.id !== "my-space",
);

/** The rows that fit in the mobile tab bar. */
export const MOBILE_PRIMARY: NavItemDef[] = NAV_ITEMS.filter((i) => i.mobilePrimary).slice(0, 5);

/** Items listed in the mobile hamburger drawer, grouped by section. */
export const SHEET_SECTIONS: NavSectionDef[] = NAV_VISIBLE_SECTIONS.map((s) => ({
  ...s,
  items: s.items.filter((i) => i.inSheet),
})).filter((s) => s.items.length > 0);

const under = (pathname: string, prefix: string, exact?: boolean): boolean => {
  if (exact) return pathname === prefix;
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
};

/**
 * Whether a nav row should read as the current page.
 *
 * Pure and declarative — pass the raw pathname, there are no special cases.
 */
export const isNavItemActive = (pathname: string, item: NavItemDef): boolean => {
  const hits = item.match.some((m) => under(pathname, m, item.exact));
  if (!hits) return false;
  return !(item.exclude ?? []).some((x) => under(pathname, x, item.exact));
};

/** Look up one item by id. */
export const getNavItem = (id: string): NavItemDef | undefined => NAV_ITEMS.find((i) => i.id === id);

/**
 * Expandable-group metadata for the desktop sidebar's My Space section.
 *
 * The rows themselves come from `NAV_ITEMS` so names, paths, and icons cannot
 * drift between the flat rows (guests / collapsed) and the expandable ones.
 */
export const MY_SPACE_GROUP_KEYS = [
  "my-topics",
  "vocabulary",
  "collections",
  "notes",
  "study-groups",
  "ai-chat",
] as const;

export type MySpaceGroupKey = (typeof MY_SPACE_GROUP_KEYS)[number];

export const MY_SPACE_EMPTY_TEXT: Record<MySpaceGroupKey, string> = {
  "my-topics": "No topics yet",
  vocabulary: "No words saved yet",
  collections: "No collections yet",
  notes: "No notes yet",
  "study-groups": "No groups joined yet",
  "ai-chat": "No conversations yet",
};
/**
 * Drop entries a signed-out visitor cannot use.
 *
 * Every `requiresAuth` route is behind `RequireAuth`, so offering those rows
 * to a guest just hands them a login screen. Filtering here keeps the rail
 * honest about what a guest can actually do, instead of advertising the whole
 * product behind a wall.
 */
export const visibleForAuth = (
  items: NavItemDef[],
  isAuthenticated: boolean | undefined,
): NavItemDef[] => (isAuthenticated ? items : items.filter((i) => !i.requiresAuth));

/** `visibleForAuth` applied to a whole section list. */
export const sectionsForAuth = (
  sections: NavSectionDef[],
  isAuthenticated: boolean | undefined,
): NavSectionDef[] =>
  sections
    .map((s) => ({ ...s, items: visibleForAuth(s.items, isAuthenticated) }))
    .filter((s) => s.items.length > 0);

/** The My Space section as visible rows. */
export const MY_SPACE_SECTION: NavSectionDef = NAV_VISIBLE_SECTIONS.find(
  (s) => s.id === "my-space"
)!;

/**
 * My Space items with no data source to expand, so they must render as plain
 * rows even when the signed-in sidebar is showing expandable groups for the
 * rest of the section.
 *
 * This is not redundant: the groups branch *replaces* the My Space section
 * rather than filtering it, so anything absent from `MY_SPACE_GROUP_KEYS`
 * would vanish for signed-in users. Progress is exactly that case.
 */
export const MY_SPACE_PLAIN_ITEMS: NavItemDef[] = MY_SPACE_SECTION.items.filter(
  (i) => !MY_SPACE_GROUP_KEYS.includes(i.id as MySpaceGroupKey),
);
